/**
 * React Query hooks for work-account engagement: streaks, badges and weekly
 * goals (GAMIFICATION feature flag).
 *
 * Every query is gated on `useEngagementEnabled()` — flag on, work account,
 * org loaded — so no request leaves for a personal space or a workspace that
 * hasn't turned the feature on.
 */

import { useAccountType } from '@/hooks/useAccountType'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { engagementApi } from '@/lib/api/engagement.api'
import { useOrganizationStore } from '@/store/organization.store'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  CreateGoalDto,
  EngagementMeResponse,
  UpdateEngagementPreferencesDto,
} from '@scrambl/shared'

import { queryKeys } from '../query-keys'

/** GAMIFICATION is on, this is a work account, and an org is loaded. */
export function useEngagementEnabled(): boolean {
  const { checkFeatureFlag } = useFeatureFlags()
  const { isPersonal } = useAccountType()
  const { currentOrganization } = useOrganizationStore()
  return (
    !isPersonal && !!currentOrganization?.id && checkFeatureFlag('GAMIFICATION')
  )
}

/** GET /engagement/me — streak, this week, goals, badges, unseen unlocks. */
export function useEngagementMe(options?: { enabled?: boolean }) {
  const enabled = useEngagementEnabled()
  return useQuery({
    queryKey: queryKeys.engagement.me(),
    queryFn: () => engagementApi.getMe(),
    enabled: enabled && options?.enabled !== false,
    staleTime: 60 * 1000,
    // An unlock earned on another device shows up when you come back.
    refetchOnWindowFocus: true,
  })
}

/** GET /engagement/achievements — the badge catalog with earned state. */
export function useAchievements(options?: { enabled?: boolean }) {
  const enabled = useEngagementEnabled()
  return useQuery({
    queryKey: queryKeys.engagement.achievements(),
    queryFn: () => engagementApi.getAchievements(),
    enabled: enabled && options?.enabled !== false,
    staleTime: 5 * 60 * 1000,
  })
}

/** GET /engagement/goals/suggestions — Benny's targets from your history. */
export function useGoalSuggestions(options?: { enabled?: boolean }) {
  const enabled = useEngagementEnabled()
  return useQuery({
    queryKey: queryKeys.engagement.goalSuggestions(),
    queryFn: () => engagementApi.getGoalSuggestions(),
    enabled: enabled && options?.enabled !== false,
    staleTime: 10 * 60 * 1000,
  })
}

/** A teammate's shared badges (empty unless they chose to share). */
export function useMemberBadges(memberId: string | undefined) {
  const enabled = useEngagementEnabled()
  return useQuery({
    queryKey: queryKeys.engagement.memberBadges(memberId ?? ''),
    queryFn: () => engagementApi.getMemberBadges(memberId!),
    enabled: enabled && !!memberId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

/**
 * Mark unlocks as celebrated. Optimistically drops them from `me.unseen` so the
 * unlock queue can never replay one while the request is in flight.
 */
export function useAckUnlocks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (unlocks: { id: string; kind: 'achievement' | 'goal' }[]) =>
      engagementApi.ackUnlocks({
        achievementIds: unlocks
          .filter(unlock => unlock.kind === 'achievement')
          .map(unlock => unlock.id),
        goalIds: unlocks
          .filter(unlock => unlock.kind === 'goal')
          .map(unlock => unlock.id),
      }),
    onMutate: unlocks => {
      const ids = new Set(unlocks.map(unlock => unlock.id))
      queryClient.setQueryData<EngagementMeResponse>(
        queryKeys.engagement.me(),
        current =>
          current
            ? {
                ...current,
                unseen: current.unseen.filter(unlock => !ids.has(unlock.id)),
              }
            : current,
      )
    },
    meta: { silent: true },
  })
}

function useInvalidateEngagement() {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.engagement.all })
}

/** Create a goal, or change the target of an existing one. */
export function useSetGoal() {
  const invalidate = useInvalidateEngagement()
  return useMutation({
    mutationFn: (input: CreateGoalDto & { id?: string }) =>
      input.id
        ? engagementApi.updateGoal(input.id, input.target)
        : engagementApi.createGoal({
            metric: input.metric,
            target: input.target,
            source: input.source,
          }),
    onSuccess: () => void invalidate(),
  })
}

export function useDeleteGoal() {
  const invalidate = useInvalidateEngagement()
  return useMutation({
    mutationFn: (goalId: string) => engagementApi.deleteGoal(goalId),
    onSuccess: () => void invalidate(),
  })
}

export function useUpdateEngagementPreferences() {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateEngagement()
  return useMutation({
    mutationFn: (patch: UpdateEngagementPreferencesDto) =>
      engagementApi.updatePreferences(patch),
    onSuccess: preferences => {
      queryClient.setQueryData<EngagementMeResponse>(
        queryKeys.engagement.me(),
        current => (current ? { ...current, preferences } : current),
      )
      void invalidate()
    },
  })
}
